import React, {useCallback, useState} from 'react';
import {TagData} from 'stores/files/FileUploadStore';
import Button from '@material-ui/core/Button';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import User from 'records/User';
import {Library} from '@store/@types/records/Library';
import {List, Map, Set} from 'immutable';
import './LibraryTabContent.sass';
import {CommentId, UserComment} from 'records/UserComment';
import LibraryHighlightSource from 'sources/library/LibraryHighlightSource';
import {associateBy} from 'utils/associateBy';
import {LibraryTagInput} from '@components/call-card/LibraryTagInput';
import {LibrarySelect} from '@components/call-card/LibrarySelect';
import {LibraryHighlightSelect} from '@components/call-card/LibraryHighlightSelect';
import {LibraryHighlight} from '@components/call-card/LibraryTabContent.utils';
import {LoadingSpinner} from '@components/loading/LoadingSpinner';
import {Int} from '@store/@types/util';
import {errorToast, toast} from '@store/toast/Toast.utils';
import {addFileToLibrary} from 'sources/library/LibrarySource.utils';

const LIBRARY_FETCH_ERROR = 'Error fetching libraries';

interface LibraryTabContentProps {
  libraries: Library[];
  tags: List<TagData>;
  callId: FileUploadId;
  sessionUser: User;
  userHighlights: List<UserComment>;
  // used in add to library dialog, since we already know the highlight to use
  preSelectedHighlight?: UserComment;
  onClose?: () => void;
}

/**
 * This component is a bit more complex than it needs to be.
 * A lot of that is due to the overly complex API behind it.
 * When we get a new API for this, try to clean it up as much as possible.
 */
export const LibraryTabContent: React.FC<LibraryTabContentProps> = ({
  libraries,
  tags,
  callId,
  sessionUser,
  userHighlights,
  preSelectedHighlight,
  onClose,
}) => {
  const [library, setLibrary] = useState<Library | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedHighlights, setSelectedHighlights] = useState(
    preSelectedHighlight?.id ? Set([preSelectedHighlight.id]) : Set<CommentId>()
  );
  const [libraryHighlights, setLibraryHighlights] = useState(Map<CommentId, LibraryHighlight>());
  const [fullCallSelected, setFullCallSelected] = useState(!preSelectedHighlight);
  const [submitting, setSubmitting] = useState(false);

  const fetchHighlights = useCallback(
    async (library: Library) => {
      try {
        const highlights: LibraryHighlight[] =
          await LibraryHighlightSource.fetchLibraryHighlights.remote({}, library.id, callId);
        const userHighlights = highlights
          .filter(h => h.userId === sessionUser.id)
          .map(h => h.highlightId);
        setLibraryHighlights(associateBy(highlights, h => h.highlightId));
        if (!preSelectedHighlight) {
          setSelectedHighlights(Set(userHighlights));
        }
      } catch {
        errorToast(LIBRARY_FETCH_ERROR);
      }
    },
    [setSelectedHighlights, setLibraryHighlights, sessionUser, callId, preSelectedHighlight]
  );

  const submit = async () => {
    if (!library || submitting) return;

    setSubmitting(true);
    try {
      const currentHighlightsForUser = libraryHighlights
        .filter(h => h.userId === sessionUser.id)
        .keySeq()
        .toSet();
      const changedHighlights = getChangedHighlights(
        currentHighlightsForUser,
        selectedHighlights,
        libraryHighlights.toList(),
        isFullCall
      );

      // TODO replace this horrible api
      await addFileToLibrary(callId, library.id, selectedTags, changedHighlights);
      setSelectedTags([]);
      onClose?.();
      toast('Saved');
    } catch (e) {
      console.error(e);
      errorToast();
    } finally {
      setSubmitting(false);
    }
  };

  const hasOtherUserHighlights = libraryHighlights.some(h => h.userId === sessionUser.id);
  const isFullCall = fullCallSelected && !hasOtherUserHighlights;

  return (
    <div className="library-tab-content">
      <LibrarySelect
        library={library}
        libraries={libraries}
        setLibrary={setLibrary}
        setSelectedTags={setSelectedTags}
        libraryHighlights={libraryHighlights}
        fetchHighlights={fetchHighlights}
      />
      <LibraryTagInput
        selectedTags={selectedTags}
        library={library}
        setSelectedTags={setSelectedTags}
        tags={tags}
        callId={callId}
      />
      {!preSelectedHighlight && (
        <LibraryHighlightSelect
          library={library}
          userHighlights={userHighlights}
          isFullCall={isFullCall}
          hasOtherUserHighlights={hasOtherUserHighlights}
          setFullCallSelected={setFullCallSelected}
          selectedHighlights={selectedHighlights}
          setSelectedHighlights={setSelectedHighlights}
        />
      )}
      {onClose ? (
        <div className="dialog-actions">
          <Button color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={submit}
            startIcon={<LoadingSpinner isLoading={submitting} />}>
            Add to Library
          </Button>
        </div>
      ) : (
        <Button
          className="submit"
          disabled={!library || submitting}
          variant="contained"
          color="primary"
          onClick={submit}>
          <LoadingSpinner isLoading={submitting} />
          Save
        </Button>
      )}
    </div>
  );
};

/**
 * @VisibleForTesting
 */
export function getChangedHighlights(
  currentHighlightsForUser: Set<CommentId>,
  selectedHighlights: Set<CommentId>,
  libraryHighlights: List<LibraryHighlight>,
  isFullCall: boolean
): {added: Set<CommentId>; removed: Set<Int>} {
  if (isFullCall) {
    return {added: Set(), removed: Set()};
  }
  const shared = currentHighlightsForUser.intersect(selectedHighlights);
  const added = selectedHighlights.subtract(shared);
  // Note, we don't use the actual highlight id for removal. We use the id that we get from
  // python for some reason, which I guess is the id for some junction table???
  const removedHighlights = currentHighlightsForUser.subtract(shared);
  const removed = removedHighlights.map(
    id => libraryHighlights.find(lh => lh.highlightId === id)!.id
  );
  return {added, removed};
}
